import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "./Header";
import SideNav from "./SideNav";
import Footer from "./Footer";
import PageHeader from "./PageHeader";
import { getUserInitials } from "../utils/userUtils";
import { mapLanguage, mapLevel, getQuestionsLabel } from "../utils/languageUtils";
import { speakWithAvatar, stopAvatarSpeech, isAvatarSpeaking } from "../ttsPlayer";
import { startSTT, stopSTT } from "../sttClient";
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import "./Common.css";
import "./Practice.css";
import "./Learn.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/lb-api";

// Independent Audio Player Component
// Supports both URL and base64 encoded audio
const AudioPlayer = ({ audioUrl, audioBase64, audioFormat = 'mp3' }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Determine audio source: prefer base64, fallback to URL
    let src = '';
    if (audioBase64) {
      src = `data:audio/${audioFormat};base64,${audioBase64}`;
    } else if (audioUrl) {
      src = audioUrl;
    }
    if (!src) return;

    audio.src = src;
    audio.load();

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("canplay", updateDuration);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("canplay", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioUrl, audioBase64, audioFormat]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch((err) => {
        console.error("Audio play failed:", err);
        setIsPlaying(false);
      });
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audio.currentTime = percentage * duration;
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="listening-audio-player-wrapper">
      <div className="listening-audio-player">
        <button className="listening-play-button" onClick={handlePlayPause}>
          {isPlaying ? (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="2" y="2" width="2" height="8" fill="#202020" />
              <rect x="8" y="2" width="2" height="8" fill="#202020" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 2L10 6L3 10V2Z" fill="#202020" />
            </svg>
          )}
        </button>

        <div
          className="listening-waveform-container"
          onClick={handleProgressClick}
        >
          <svg
            width="100%"
            height="24"
            viewBox="0 0 350 24"
            preserveAspectRatio="none"
          >
            {Array.from({ length: 80 }).map((_, i) => {
              const heights = [
                4, 8, 12, 16, 20, 24, 20, 16, 12, 8, 4, 8, 12, 16, 20, 16, 12,
                8, 4, 8,
              ];
              const height = heights[i % heights.length];
              const x = i * 4.375;
              const y = (24 - height) / 2;
              const progress = duration > 0 ? currentTime / duration : 0;
              const isPlayed = i < progress * 80;
              return (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width="2"
                  height={height}
                  fill={isPlayed ? "#202020" : "#707070"}
                />
              );
            })}
          </svg>
        </div>
      </div>

      <div className="listening-time-display">{formatTime(duration)} sec</div>
      <audio ref={audioRef} style={{ display: "none" }} />
    </div>
  );
};

const ListeningPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);
  const rightContentRef = useRef(null);
  const inputRef = useRef(null);
  const promptsOverlayRef = useRef(null);
  const translationPanelRef = useRef(null);
  const conversationIdRef = useRef(null);
  const {
    language: initialLanguage = "german",
    proficiencyLevel: initialLevel = "beginner",
    avatar: initialAvatar = "Max-business.mp4", // Default to Soren (same as SpeakingPage)
  } = location.state || {};

  const [language, setLanguage] = useState(initialLanguage);
  const [proficiencyLevel, setProficiencyLevel] = useState(initialLevel);
  const [inputText, setInputText] = useState("");
  const [showPrompts, setShowPrompts] = useState(true);
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState("Listening");
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const skillDropdownRef = useRef(null);
  const [currentAvatar, setCurrentAvatar] = useState(initialAvatar);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isReadingWelcome, setIsReadingWelcome] = useState(false);
  const [sttActive, setSttActive] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [partialTranscript, setPartialTranscript] = useState("");
  const languageToISOCode = (lang) => {
    const mapping = {
      'german': 'de', 'english': 'en', 'french': 'fr',
      'spanish': 'es', 'italian': 'it', 'japanese': 'ja',
      'de': 'de', 'en': 'en', 'fr': 'fr', 'es': 'es', 'it': 'it', 'ja': 'ja'
    };
    return mapping[lang.toLowerCase()] || 'de';
  };

  // Convert proficiency level to kebab-case format for PageHeader
  const levelToKebabCase = (level) => {
    const mapping = {
      'beginner': 'beginner-a1', 'elementary': 'elementary-a2',
      'pre-intermediate': 'pre-intermediate-b1', 'intermediate': 'intermediate-b2',
      'advanced': 'advanced-c1', 'proficient': 'proficient-c2',
      'beginner-a1': 'beginner-a1', 'elementary-a2': 'elementary-a2',
      'pre-intermediate-b1': 'pre-intermediate-b1', 'intermediate-b2': 'intermediate-b2',
      'advanced-c1': 'advanced-c1', 'proficient-c2': 'proficient-c2',
      // Japanese JLPT levels
      'beginner-n5': 'beginner-n5', 'elementary-n4': 'elementary-n4',
      'pre-intermediate-n3': 'pre-intermediate-n3', 'intermediate-n2': 'intermediate-n2',
      'advanced-n1': 'advanced-n1'
    };
    return mapping[level.toLowerCase()] || level;
  };
  const [showPromptsOverlay, setShowPromptsOverlay] = useState(false);
  const [hasSubmittedPrompt, setHasSubmittedPrompt] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Prompts from database
  const [prompts, setPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);

  // Exercise management for continuous flow
  const [exercises, setExercises] = useState([]); // Array of completed exercises
  const [currentExercise, setCurrentExercise] = useState(null); // Current exercise being worked on

  // Translation state
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatingText, setTranslatingText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [translationLabel, setTranslationLabel] = useState("English");
  const [isReadingTranslation, setIsReadingTranslation] = useState(false);
  const [transcriptText, setTranscriptText] = useState(""); // For listening page transcript display
  const [visibleCaptionsId, setVisibleCaptionsId] = useState(null); // Track which exercise captions are shown

  // Say It Aloud state
  const [readingMessageId, setReadingMessageId] = useState(null);
  const [isReading, setIsReading] = useState(false);

  // Ref for the latest message to scroll to
  const latestMessageRef = useRef(null);

  usePageTimeTracker('practice-listening', user);

  // Cleanup STT on unmount (page navigation)
  useEffect(() => {
    return () => { stopSTT(); };
  }, []);

  // Helper function to scroll to latest content
  const scrollToLatest = () => {
    if (latestMessageRef.current) {
      latestMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  };
  // Close prompts overlay when clicking outside
  useEffect(() => {
    if (!showPromptsOverlay) return;
    const handleClickOutside = (e) => {
      if (promptsOverlayRef.current && !promptsOverlayRef.current.contains(e.target)) {
        setShowPromptsOverlay(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPromptsOverlay]);

  // Close translation panel when clicking outside
  useEffect(() => {
    if (!showTranslation) return;
    const handleClickOutside = (e) => {
      if (translationPanelRef.current && !translationPanelRef.current.contains(e.target)) {
        setShowTranslation(false);
        setTranslatingText('');
        setTranslatedText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTranslation]);

  // Close skill dropdown when clicking outside
  useEffect(() => {
    if (!showSkillDropdown) return;
    const handleClickOutside = (e) => {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(e.target)) {
        setShowSkillDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSkillDropdown]);

  useEffect(() => {
    let cancelled = false;
    const fetchPrompts = async () => {
      const mappedLanguage = mapLanguage(language);
      let mappedLevel = mapLevel(proficiencyLevel);

      // Fix level-language mismatch: use default level if JLPT with non-Japanese or vice versa
      const isJLPT = /^N[1-5]$/i.test(mappedLevel);
      const isJapanese = language === 'ja' || language === 'japanese';
      if (isJLPT && !isJapanese) mappedLevel = 'A1';
      if (!isJLPT && isJapanese && mappedLevel) mappedLevel = 'N5';

      try {
        setIsLoadingPrompts(true);

        const response = await fetch(`${API_BASE_URL}/practice/prompts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            skill: "Listening",
            language: mappedLanguage,
            proficiencyLevel: mappedLevel,
          }),
        });

        if (cancelled) return;
        const data = await response.json();

        if (data.success && data.prompts) {
          const promptTexts = data.prompts.map((p) => p.text);
          setPrompts(promptTexts);
        } else {
          console.error("No prompts in response:", data);
          // Fallback to default prompts if DB returns no data
          setPrompts([
            "self introduction",
            "German listening with translations",
            "Short German audio for beginners",
            "I want a quick recap",
            "Practice German pronunciation",
            "Learn German vocabulary",
          ]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching prompts:", error);
        // Fallback to default prompts if DB is not connected
        setPrompts([
          "self introduction",
          "German listening with translations",
          "Short German audio for beginners",
          "I want a quick recap",
          "Practice German pronunciation",
          "Learn German vocabulary",
        ]);
      } finally {
        if (!cancelled) setIsLoadingPrompts(false);
      }
    };

    fetchPrompts();
    return () => { cancelled = true; };
  }, [language, proficiencyLevel]);

  // Display prompts based on showAllPrompts state
  const displayedPrompts = showAllPrompts ? prompts : prompts.slice(0, 3);

  const breadcrumbs = [
    { label: "Home", path: "/", current: false },
    { label: "Explore Learning", path: "/explore-learning", current: false },
    { label: "Practice", path: "/explore-learning", current: false }, // Goes to explore-learning
    { label: "Language Skills", current: true },
  ];

  // Skill options for dropdown
  const skills = [
    { value: "reading", label: "Reading", route: "/practice-reading" },
    { value: "writing", label: "Writing", route: "/practice-writing" },
    { value: "listening", label: "Listening", route: "/practice-listening" },
    { value: "speaking", label: "Speaking", route: "/practice-speaking" },
  ];

  const currentSkill = "listening";

  // Get avatar initial for display (S for Soren, N for Nebula)
  const getAvatarInitial = () => {
    const avatarLower = String(currentAvatar).toLowerCase();
    // Soren (male) = 'Max-business.mp4' → 'S'
    // Nebula (female) = 'talkingavater.mp4' → 'N'
    if (avatarLower.includes("max") || avatarLower.includes("soren")) {
      return "S";
    }
    return "N";
  };

  // Get avatar name for display
  const getAvatarName = () => {
    const avatarLower = String(currentAvatar).toLowerCase();
    if (avatarLower.includes("max") || avatarLower.includes("soren")) {
      return "Soren";
    }
    return "Nebula";
  };

  // Get avatar image
  const getAvatarImage = () => {
    const avatarLower = String(currentAvatar).toLowerCase();
    if (avatarLower.includes("max") || avatarLower.includes("soren")) {
      return "/images/maleavatarinskillpage.png";
    }
    return "/images/FemaleAvatar.svg";
  };

  // Get welcome message based on selected language
  const getWelcomeMessage = () => {
    const avatarName = getAvatarName();
    const languageLower = (language || '').toLowerCase();

    const welcomeMessages = {
      german: `Hi, I am ${avatarName}, let's practice German.`,
      deutsch: `Hi, I am ${avatarName}, let's practice German.`,
      de: `Hi, I am ${avatarName}, let's practice German.`,
      french: `Hi, I am ${avatarName}, let's practice French.`,
      français: `Hi, I am ${avatarName}, let's practice French.`,
      fr: `Hi, I am ${avatarName}, let's practice French.`,
      spanish: `Hi, I am ${avatarName}, let's practice Spanish.`,
      español: `Hi, I am ${avatarName}, let's practice Spanish.`,
      espanol: `Hi, I am ${avatarName}, let's practice Spanish.`,
      es: `Hi, I am ${avatarName}, let's practice Spanish.`,
      italian: `Hi, I am ${avatarName}, let's practice Italian.`,
      italiano: `Hi, I am ${avatarName}, let's practice Italian.`,
      it: `Hi, I am ${avatarName}, let's practice Italian.`,
      japanese: `Hi, I am ${avatarName}, let's practice Japanese.`,
      ja: `Hi, I am ${avatarName}, let's practice Japanese.`,
      english: `Hi, I am ${avatarName}, let's practice English.`,
      en: `Hi, I am ${avatarName}, let's practice English.`,
    };

    return (
      welcomeMessages[languageLower] ||
      `Hi, I am ${avatarName}, let's practice.`
    );
  };

  // Get listening instruction text based on selected language
  const getListeningInstruction = () => {
    const lang = (language || 'german').toLowerCase();
    if (lang === 'german' || lang === 'deutsch' || lang === 'de') return 'Hören Sie den Dialog und beantworten Sie die folgenden Fragen.';
    if (lang === 'french' || lang === 'français' || lang === 'fr') return 'Écoutez le dialogue et répondez aux questions suivantes.';
    if (lang === 'spanish' || lang === 'español' || lang === 'espanol' || lang === 'es') return 'Escuche el diálogo y responda las siguientes preguntas.';
    if (lang === 'italian' || lang === 'italiano' || lang === 'it') return 'Ascolta il dialogo e rispondi alle seguenti domande.';
    if (lang === 'japanese' || lang === '日本語' || lang === 'ja') return '対話を聞いて、次の質問に答えてください。';
    if (lang === 'english' || lang === 'en') return 'Listen to the dialogue and answer the following questions.';
    return 'Listen to the dialogue and answer the following questions.';
  };

  // Get the full language name for API calls
  const getSelectedLanguageName = () => {
    const lang = (language || 'german').toLowerCase();
    const map = { german: 'German', deutsch: 'German', de: 'German', french: 'French', français: 'French', fr: 'French', spanish: 'Spanish', español: 'Spanish', espanol: 'Spanish', es: 'Spanish', italian: 'Italian', italiano: 'Italian', it: 'Italian', japanese: 'Japanese', '日本語': 'Japanese', ja: 'Japanese', english: 'English', en: 'English' };
    return map[lang] || 'German';
  };

  // Get feedback text based on selected language
  const getFeedbackText = (isCorrect) => {
    const lang = (language || 'german').toLowerCase();
    
    // Handle both language codes and full names
    if (lang === 'german' || lang === 'deutsch' || lang === 'de') {
      return isCorrect ? 'Das ist richtig!' : 'Das ist leider falsch.';
    } else if (lang === 'french' || lang === 'français' || lang === 'fr') {
      return isCorrect ? 'C\'est correct!' : 'C\'est malheureusement faux.';
    } else if (lang === 'spanish' || lang === 'español' || lang === 'espanol' || lang === 'es') {
      return isCorrect ? '¡Eso es correcto!' : 'Eso es incorrecto.';
    } else if (lang === 'italian' || lang === 'italiano' || lang === 'it') {
      return isCorrect ? 'È corretto!' : 'È sbagliato.';
    } else if (lang === 'japanese' || lang === '日本語' || lang === 'ja') {
      return isCorrect ? '正解です！' : '残念ながら間違っています。';
    } else if (lang === 'english' || lang === 'en') {
      return isCorrect ? 'That\'s correct!' : 'That\'s incorrect.';
    } else {
      return isCorrect ? 'Correct!' : 'Incorrect.';
    }
  };

  const avatarInitial = getAvatarInitial();
  const avatarName = getAvatarName();
  const avatarImage = getAvatarImage();
  const userInitials = getUserInitials(user);

  // Voice selection based on avatar and language (like SpeakingSkill)
  const getVoiceForAvatar = (avatar, language) => {
    const avatarLower = String(avatar).toLowerCase();
    const isMale =
      avatarLower.includes("allthebest") ||
      avatarLower.includes("max") ||
      avatarLower.includes("soren");

    const voiceMap = {
      de: { male: "Hans", female: "Marlene" },
      en: { male: "Matthew", female: "Joanna" },
      fr: { male: "Mathieu", female: "Celine" },
      es: { male: "Enrique", female: "Conchita" },
      it: { male: "Giorgio", female: "Carla" },
      ja: { male: "Takumi", female: "Mizuki" },
    };

    const langCode =
      language === "german"
        ? "de"
        : language === "english"
          ? "en"
          : language === "french"
            ? "fr"
            : language === "spanish"
              ? "es"
              : language === "italian"
                ? "it"
                : language === "japanese"
                  ? "ja"
                  : "de";

    return voiceMap[langCode]?.[isMale ? "male" : "female"] || "Joanna";
  };

  const handleLanguageChange = (newLanguage) => {
    // Convert ISO code back to full language name
    const isoToLanguage = {
      'de': 'german', 'en': 'english', 'fr': 'french',
      'es': 'spanish', 'it': 'italian', 'ja': 'japanese'
    };
    setLanguage(isoToLanguage[newLanguage] || newLanguage);
  };

  const handleLevelChange = (newLevel) => {
    // Just pass through the kebab-case level directly — mapLevel handles all formats
    setProficiencyLevel(newLevel);
  };

  

  // Translation function
  const handleTranslate = async (textToTranslate, overrideSource, overrideTarget) => {
    setTranscriptText(""); // Clear transcript for non-transcript translations
    setTranslatingText(textToTranslate);
    setShowTranslation(true);
    setTranslatedText("Translating...");

    const languageMap = {
      de: "German", en: "English", fr: "French", es: "Spanish", it: "Italian", ja: "Japanese",
      german: "German", english: "English", french: "French", spanish: "Spanish", italian: "Italian", japanese: "Japanese",
    };

    const selectedLang = languageMap[language?.toLowerCase()] || "German";
    const sourceLanguage = overrideSource || selectedLang;
    const targetLanguage = overrideTarget || "English";
    setTranslationLabel(targetLanguage);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/translate`,
        {
          text: textToTranslate,
          source_language: sourceLanguage,
          target_language: targetLanguage,
          level: proficiencyLevel || "A1",
          context: "listening practice",
        },
        {
          withCredentials: true,
        }
      );

      let translatedContent = "";
      if (response.data && response.data.translation) {
        translatedContent = response.data.translation;
      } else if (response.data && response.data.translatedText) {
        translatedContent = response.data.translatedText;
      } else {
        translatedContent = "Translation not available";
      }

      setTranslatedText(translatedContent);
    } catch (error) {
      console.error("Translation error:", error);
      setTranslatedText(
        "Translation service unavailable. Please try again later."
      );
    }
  };

  const handleCloseTranslation = () => {
    stopAvatarSpeech();
    setIsReadingTranslation(false);
    setShowTranslation(false);
    setTranslatingText("");
    setTranscriptText("");
  };

  const handleReadTranslation = async () => {
    if (isReadingTranslation) {
      stopAvatarSpeech();
      setIsReadingTranslation(false);
      return;
    }
    if (!translatedText || translatedText === 'Translating...') return;
    
    const cleanedText = translatedText.replace(/^\d+\s*[-–—.)\]]\s*/gm, '');
    
    const avatarLower = String(currentAvatar).toLowerCase();
    const isMale = avatarLower.includes('max') || avatarLower.includes('soren');
    const voice = isMale ? 'Matthew' : 'Joanna';
    
    setIsReadingTranslation(true);
    try {
      await speakWithAvatar(cleanedText, null, voice);
    } catch (error) {
      console.error('TTS translation error:', error);
    }
  };

  // Show Transcript: opens slide panel with transcript + English translation
  const handleShowTranscript = async (transcript) => {
    setTranscriptText(transcript);
    setTranslatingText(transcript);
    setShowTranslation(true);
    setTranslatedText("Translating...");

    try {
      const sourceLanguage = getSelectedLanguageName();
      const response = await axios.post(
        `${API_BASE_URL}/translate`,
        {
          text: transcript,
          source_language: sourceLanguage,
          target_language: "English",
          level: proficiencyLevel || "A1",
          context: "listening transcript",
        },
        { withCredentials: true }
      );
      if (response.data && response.data.translatedText) {
        setTranslatedText(response.data.translatedText);
      } else if (response.data && response.data.translation) {
        setTranslatedText(response.data.translation);
      } else {
        setTranslatedText("Translation not available.");
      }
    } catch (error) {
      console.error("Translation error:", error);
      setTranslatedText("Translation failed. Please try again.");
    }
  };

  // Say It Aloud function using TTS
  // overrideLang: optional language override (e.g. 'english' for welcome message)
  const handleReadAloud = async (text, messageId, overrideLang) => {
    if (readingMessageId === messageId && isReading) {
      stopAvatarSpeech();
      setIsReading(false);
      setReadingMessageId(null);
      return;
    }

    stopAvatarSpeech();
    window.speechSynthesis.cancel();

    setIsReading(true);
    setReadingMessageId(messageId);

    try {
      const selectedVoice = getVoiceForAvatar(currentAvatar, overrideLang || language);
      await speakWithAvatar(text, null, selectedVoice);
    } catch (error) {
      console.error("TTS error:", error);
      setIsReading(false);
      setReadingMessageId(null);
    }
  };

  // Listen for TTS completion to reset reading state
  useEffect(() => {
    if (!isReading && !isReadingTranslation) return;
    const checkTTSStatus = setInterval(() => {
      if (!isAvatarSpeaking()) {
        if (isReading) {
          setIsReading(false);
          setReadingMessageId(null);
        }
        if (isReadingTranslation) {
          setIsReadingTranslation(false);
        }
      }
    }, 500);

    return () => clearInterval(checkTTSStatus);
  }, [isReading, isReadingTranslation]);

  // Update inputText when STT transcript changes
  useEffect(() => {
    if (sttActive) {
      const combinedText = [finalTranscript, partialTranscript].filter(Boolean).join(" ");
      setInputText(combinedText);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
          inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
          inputRef.current.style.overflowY = inputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
          inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [finalTranscript, partialTranscript, sttActive]);

  // Refs for global Enter handler
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;
  const sttActiveRef = useRef(sttActive);
  sttActiveRef.current = sttActive;
  const handleSendRef = useRef(null);

  // Global Enter key handler when STT is active
  useEffect(() => {
    handleSendRef.current = handleSendMessage;
  });
  useEffect(() => {
    if (!sttActive) return;
    const handleGlobalEnter = (e) => {
      if (e.key === "Enter" && !e.shiftKey && sttActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const textToSend = inputTextRef.current?.trim();
        if (textToSend) {
          handleSendRef.current(textToSend);
        }
      }
    };
    document.addEventListener("keydown", handleGlobalEnter, true);
    return () => document.removeEventListener("keydown", handleGlobalEnter, true);
  }, [sttActive]);

  const handlePromptClick = (prompt) => {
    setInputText(prompt);
    // Focus and auto-grow
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
        inputRef.current.style.overflowY = inputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
      }
    }, 0);
  };

  // Handle microphone click for STT
  const handleMicClick = async () => {
    if (sttActive) {
      // Stop recording — keep current inputText as-is
      stopSTT();
      setSttActive(false);
      setFinalTranscript("");
      setPartialTranscript("");
    } else {
      setFinalTranscript(inputText);
      setPartialTranscript("");
      setSttActive(true);
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/stt`;
      const langMap = { 'german': 'de-DE', 'english': 'en-US', 'french': 'fr-FR', 'spanish': 'es-ES', 'italian': 'it-IT', 'japanese': 'ja-JP', 'de': 'de-DE', 'en': 'en-US', 'fr': 'fr-FR', 'es': 'es-ES', 'it': 'it-IT', 'ja': 'ja-JP' };
      const sttLang = langMap[language?.toLowerCase()] || 'de-DE';
      startSTT(wsUrl, { language: sttLang }, ({ text, isPartial }) => {
        if (!isPartial) {
          setFinalTranscript(prev => prev ? `${prev} ${text}` : text);
          setPartialTranscript("");
        } else {
          setPartialTranscript(text);
        }
      });
    }
  };

  const handleSendMessage = async (overrideText) => {
    // Capture current text before stopping STT
    const currentText = overrideText || inputText.trim();

    // Always stop STT if active
    if (sttActive) {
      stopSTT();
      setSttActive(false);
      setFinalTranscript("");
      setPartialTranscript("");
    }

    if (!currentText) return;

    const promptText = currentText;
    setInputText("");
    if (inputRef.current) { inputRef.current.style.height = 'auto'; inputRef.current.style.overflowY = 'hidden'; }
    setShowPrompts(false);
    setHasSubmittedPrompt(true);
    setShowPromptsOverlay(false);

    // Save current exercise if one exists (regardless of completion)
    if (currentExercise) {
      setExercises((prev) => [...prev, currentExercise]);
    }

    // Create new exercise with user prompt immediately visible
    const newExercise = {
      id: Date.now(),
      promptText: promptText,
      audioUrl: null,
      audioBase64: null,
      audioFormat: 'mp3',
      transcript: null,
      questions: [],
      answers: {},
      conversationMessages: [],
      isLoading: true,
      instructionText: getListeningInstruction(),
      questionsLabel: getQuestionsLabel(language),
    };

    setCurrentExercise(newExercise);
    setIsLoading(true);

    // Scroll to show the user's new prompt
    setTimeout(() => {
      scrollToLatest();
    }, 100);

    try {
      const mappedLevel = mapLevel(proficiencyLevel);
      const mappedLanguage = mapLanguage(language);
      const mappedAvatar = getAvatarName();

      const response = await fetch(
        `${API_BASE_URL}/practice/listening/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            language: mappedLanguage,
            level: mappedLevel,
            query: promptText,
            dialect: "standard",
            avatar: mappedAvatar,
          }),
        },
      );

      const data = await response.json();

      // Check for error in response
      if (data?.content?.error) {
        const apiError = data.content.error;
        setCurrentExercise((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to load exercise",
          conversationMessages: [
            ...(prev?.conversationMessages || []),
            {
              type: "bot",
              text: apiError,
              timestamp: new Date(),
            },
          ],
        }));
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to generate listening exercise");
      }

      if (data?.content?.questions && data.content.questions.length > 0) {
      }

      // Update exercise with API response
      // New API returns: audio_base64, audio_format, transcript, questions
      // Fallback: also supports legacy audio_url
      const updatedExercise = {
        ...newExercise,
        audioBase64: data?.content?.audio_base64 || "",
        audioFormat: data?.content?.audio_format || "mp3",
        audioUrl: data?.content?.audio_url || "",
        transcript: data?.content?.transcript || "",
        questions: data?.content?.questions || [],
        isLoading: false,
      };
      setCurrentExercise(updatedExercise);

      // Auto-scroll to the new content
      setTimeout(() => {
        scrollToLatest();
      }, 200);

      // Silently save conversation to DB
      try {
        if (!conversationIdRef.current) {
          const convRes = await axios.post(`${API_BASE_URL}/chat/conversation`, {
            enterprise_id: user?.email || 'local-user',
            page_name: 'practice-listening',
            title: promptText.substring(0, 100),
            language: mappedLanguage,
            level: mappedLevel
          }, { withCredentials: true });
          if (convRes.data?.conversation_id) {
            conversationIdRef.current = convRes.data.conversation_id;
          }
        }
        if (conversationIdRef.current) {
          // Build full agent response with transcript + questions
          let agentText = getListeningInstruction();
          if (updatedExercise.transcript) {
            agentText += '\n\nTranscript:\n' + updatedExercise.transcript;
          }
          if (updatedExercise.questions && updatedExercise.questions.length > 0) {
            agentText += '\n\n--- Questions ---';
            updatedExercise.questions.forEach((q, i) => {
              agentText += `\n${i + 1}. ${q.question}`;
              if (q.options) {
                q.options.forEach((opt, j) => {
                  const letter = String.fromCharCode(65 + j);
                  agentText += `\n   ${letter}. ${opt.text}`;
                });
                agentText += `\n   Correct: ${q.correct_answer}`;
              }
            });
          }
          await axios.post(`${API_BASE_URL}/chat/message`, {
            conversation_id: conversationIdRef.current,
            prompt: promptText,
            user_input: promptText,
            agent_response: agentText
          }, { withCredentials: true });
        }
      } catch (saveErr) {
        console.error('Chat save error (non-blocking):', saveErr.message);
      }
    } catch (error) {
      console.error("Error fetching listening exercise:", error);
      
      const errorMessage = "There's a problem processing your request. Please try again later or let me know if I can help.";
      
      setCurrentExercise((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to load exercise",
        conversationMessages: [
          ...(prev?.conversationMessages || []),
          {
            type: "bot",
            text: errorMessage,
            timestamp: new Date(),
          },
        ],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkillChange = (skill) => {
    setShowSkillDropdown(false);
    if (skill.value !== currentSkill) {
      // Navigate with state instead of URL parameters
      navigate(skill.route, {
        state: {
          language,
          proficiencyLevel,
          avatar: currentAvatar,
        },
      });
    }
  };

  const handleAvatarSoundToggle = () => {
    setIsMuted(!isMuted);
    // Note: TTS functionality would be integrated here if needed
  };

  // Read welcome message aloud with English voice (since it's English text)
  const handleReadWelcome = async () => {
    if (isReadingWelcome) {
      stopAvatarSpeech();
      setIsReadingWelcome(false);
      return;
    }
    stopAvatarSpeech();
    setIsReadingWelcome(true);
    const avatarLower = String(currentAvatar).toLowerCase();
    const isMale = avatarLower.includes('max') || avatarLower.includes('soren');
    const englishVoice = isMale ? 'Matthew' : 'Joanna';
    try {
      await speakWithAvatar(getWelcomeMessage(), null, englishVoice);
    } catch (error) {
      console.error('TTS error:', error);
    }
    setIsReadingWelcome(false);
  };

  // Check TTS completion for welcome message
  useEffect(() => {
    if (!isReadingWelcome) return;
    const check = setInterval(() => {
      if (!isAvatarSpeaking()) {
        setIsReadingWelcome(false);
        clearInterval(check);
      }
    }, 500);
    return () => clearInterval(check);
  }, [isReadingWelcome]);

  const handleChangeAvatar = () => {
    // Toggle between Soren (male) and Nebula (female)
    // Soren = 'Max-business.mp4', Nebula = 'talkingavater.mp4'
    const isSoren =
      currentAvatar.includes("Max") ||
      currentAvatar.toLowerCase().includes("soren");
    const newAvatar = isSoren ? "talkingavater.mp4" : "Max-business.mp4";
    setCurrentAvatar(newAvatar);
  };

  

  

  

  

  

  const handleNewChat = () => {
    // Reset all state to initial values
    setExercises([]);
    setCurrentExercise(null);
    setShowPrompts(true);
    setHasSubmittedPrompt(false);
    setInputText("");
    setShowPromptsOverlay(false);
    setIsLoading(false);
    conversationIdRef.current = null;
  };

  const handleAnswerSelect = (questionId, optionId) => {
    if (!currentExercise) return;

    const question = currentExercise.questions.find((q) => q.id === questionId);
    if (!question) return;

    const selectedOption = question.options.find((opt) => opt.id === optionId);
    if (!selectedOption) return;

    // Check if answer is correct - compare with question's correct_answer field
    const isCorrect = question.correct_answer === optionId;

    // Store the answer in current exercise
    setCurrentExercise((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: optionId,
      },
    }));

    // Immediately add user's answer as a message
    const questionIndex = currentExercise.questions.findIndex(
      (q) => q.id === questionId,
    );
    const userAnswerMessage = {
      type: "user",
      text: selectedOption.text,
      questionNumber: questionIndex + 1,
      timestamp: new Date(),
    };

    setCurrentExercise((prev) => ({
      ...prev,
      conversationMessages: [...prev.conversationMessages, userAnswerMessage],
    }));

    // Add feedback message after short delay
    setTimeout(() => {
      const feedbackText = getFeedbackText(isCorrect);
      const feedbackMessage = {
        type: "bot",
        text: feedbackText,
        isCorrect: isCorrect,
        timestamp: new Date(),
      };

      setCurrentExercise((prev) => ({
        ...prev,
        conversationMessages: [...prev.conversationMessages, feedbackMessage],
      }));

      // Save answer + feedback to DB
      if (conversationIdRef.current) {
        const userAnswer = `Q${questionIndex + 1}: ${question.question}\nSelected: ${selectedOption.text}`;
        const botFeedback = `${feedbackText} (${isCorrect ? 'Correct' : 'Incorrect'})`;
        axios.post(`${API_BASE_URL}/chat/message`, {
          conversation_id: conversationIdRef.current,
          user_input: userAnswer,
          agent_response: botFeedback
        }, { withCredentials: true }).catch(e => console.error('Chat save error:', e.message));
      }

      // Scroll to feedback after it's added
      setTimeout(() => {
        scrollToLatest();
      }, 150);

      // Move to next question after showing feedback (regardless of correct/wrong)
      setTimeout(() => {
        if (questionIndex < currentExercise.questions.length - 1) {
          // Add next question as bot message
          const nextQuestion = currentExercise.questions[questionIndex + 1];
          const nextQuestionMessage = {
            type: "bot",
            text: nextQuestion.question,
            isQuestion: true,
            questionData: nextQuestion,
            timestamp: new Date(),
          };
          setCurrentExercise((prev) => ({
            ...prev,
            conversationMessages: [
              ...prev.conversationMessages,
              nextQuestionMessage,
            ],
          }));

          // Scroll to show next question after it's added
          setTimeout(() => {
            scrollToLatest();
          }, 150);
        } else {
          // All questions completed
          const completionText = "You have completed all questions!";
          const completionMessage = {
            type: "bot",
            text: completionText,
            timestamp: new Date(),
          };
          setCurrentExercise((prev) => ({
            ...prev,
            conversationMessages: [
              ...prev.conversationMessages,
              completionMessage,
            ],
          }));

          // Scroll to completion message
          setTimeout(() => {
            scrollToLatest();
          }, 150);

          // Show prompts again for next exercise
          setTimeout(() => {
            setShowPrompts(true);
          }, 1000);
        }
      }, 1500); // Wait 1.5s after feedback before showing next question
    }, 300); // Small delay before showing feedback
  };



  // Helper function to render a single exercise (similar to ReadingPage)
  const renderExercise = (exercise, isCompleted = false) => {
    if (!exercise) return null;

    return (
      <React.Fragment key={exercise.id}>
        {/* User's Prompt */}
        <div
          className="practice-message-row user-message-row"
          style={{ justifyContent: "flex-end", marginTop: "16px" }}
          ref={(el) => {
            if (!isCompleted && exercise.isLoading) {
              latestMessageRef.current = el;
            }
          }}
        >
          <div
            className="practice-message-content-wrapper"
            style={{ maxWidth: "60%" }}
          >
            <div
              className="practice-user-message-text"
            >
              {exercise.promptText}
            </div>
          </div>
          <div
            className="practice-avatar-circle user-avatar"
            style={{ background: "#A100FF", marginLeft: "16px" }}
          >
            {userInitials}
          </div>
        </div>

        {/* Loading State */}
        {exercise.isLoading && (
          <div
            className="practice-message-row bot-message-row"
            style={{ marginTop: "16px" }}
          >
            <div className="practice-avatar-circle bot-avatar">
              {avatarInitial}
            </div>
            <div className="practice-message-content-wrapper">
              <div className="practice-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* Bot Response with Audio and First Question */}
        {!exercise.isLoading &&
          (exercise.audioUrl || exercise.audioBase64) &&
          exercise.questions &&
          exercise.questions.length > 0 && (
            <div
              className="practice-message-row bot-message-row"
              style={{ marginTop: "16px" }}
              ref={(el) => {
                if (!isCompleted && exercise.conversationMessages.length === 0) {
                  latestMessageRef.current = el;
                }
              }}
            >
              <div className="practice-avatar-circle bot-avatar">
                {avatarInitial}
              </div>
              <div className="practice-message-content-wrapper">
                <div
                  className="practice-bot-message-text"
                  style={{ fontWeight: "600", marginBottom: "8px" }}
                >
                  {exercise.instructionText}
                </div>

                <div
                  className="practice-message-actions"
                  style={{ marginBottom: "12px" }}
                >
                  <button 
                    className="practice-action-btn"
                    onClick={() => handleTranslate(exercise.instructionText)}
                  >
                    <img
                      src="/images/translateiconactive.svg"
                      alt="Translate"
                      width="16"
                      height="16"
                    />
                    Translate in English
                  </button>
                  <button 
                    className="practice-action-btn"
                    onClick={() => handleReadAloud(exercise.instructionText, `audio-instruction-${exercise.id}`)}
                  >
                    <img
                      src={readingMessageId === `audio-instruction-${exercise.id}` && isReading ? "/images/muteicon.svg" : "/images/readicon.svg"}
                      alt={readingMessageId === `audio-instruction-${exercise.id}` && isReading ? "Stop" : "Say It Aloud"}
                      width="16"
                      height="16"
                    />
                    {readingMessageId === `audio-instruction-${exercise.id}` && isReading ? "Stop" : "Say It Aloud"}
                  </button>
                </div>

                {/* Audio Player - independent for each exercise */}
                <AudioPlayer 
                  audioUrl={exercise.audioUrl} 
                  audioBase64={exercise.audioBase64}
                  audioFormat={exercise.audioFormat}
                />

                {/* Show Transcript toggle */}
                {exercise.transcript && (
                  <div style={{ marginTop: '8px' }}>
                    <button 
                      className="practice-action-btn"
                      onClick={() => setVisibleCaptionsId(visibleCaptionsId === exercise.id ? null : exercise.id)}
                    >
                      <img src="/images/translateiconactive.svg" alt="Transcript" width="16" height="16" />
                      {visibleCaptionsId === exercise.id ? 'Hide Transcript' : 'Show Transcript'}
                    </button>
                    {visibleCaptionsId === exercise.id && (
                      <div className="listening-captions-box">
                        <p className="listening-captions-text">{exercise.transcript}</p>
                        <button
                          className="practice-action-btn"
                          style={{ marginTop: '8px' }}
                          onClick={() => handleTranslate(exercise.transcript)}
                        >
                          <img src="/images/translateiconactive.svg" alt="Translate" width="14" height="14" />
                          Translate to English
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Questions Section - Separate message with avatar */}
        {!exercise.isLoading &&
          (exercise.audioUrl || exercise.audioBase64) &&
          exercise.questions &&
          exercise.questions.length > 0 && (
            <div
              className="practice-message-row bot-message-row"
              style={{ marginTop: "16px" }}
            >
              <div className="practice-avatar-circle bot-avatar">
                {avatarInitial}
              </div>
              <div className="practice-message-content-wrapper">
                <div className="practice-questions-section">
                  <div className="practice-questions-title">
                    {exercise.questionsLabel || getQuestionsLabel(language)}
                  </div>

                  {exercise.questions[0] && (
                    <div className="practice-question-item">
                      <div className="practice-question-text">
                        1. {exercise.questions[0].question}
                      </div>
                      <div className="practice-question-label">
                        Choose the correct answer
                      </div>
                      <div className="practice-radio-group">
                        {exercise.questions[0].options &&
                          exercise.questions[0].options.map((option) => (
                            <label
                              key={option.id}
                              className="practice-radio-option"
                            >
                              <input
                                type="radio"
                                name={`question-${exercise.id}-${exercise.questions[0].id}`}
                                value={option.id}
                                checked={
                                  exercise.answers[exercise.questions[0].id] ===
                                  option.id
                                }
                                onChange={() =>
                                  !isCompleted &&
                                  handleAnswerSelect(
                                    exercise.questions[0].id,
                                    option.id,
                                  )
                                }
                                disabled={
                                  isCompleted ||
                                  exercise.answers[exercise.questions[0].id] !==
                                    undefined
                                }
                              />
                              <span className="practice-radio-custom"></span>
                              <span className="practice-radio-text">
                                {option.text}
                              </span>
                            </label>
                          ))}
                      </div>
                      <div className="practice-message-actions" style={{marginTop: '8px'}}>
                        <button 
                          className="practice-action-btn" 
                          onClick={() => {
                            const q = exercise.questions[0];
                            const optionsText = (q.options || []).map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${opt.text}`).join('\n');
                            const questionWithOptions = `Question: ${q.question}\n\nAnswer Options:\n${optionsText}`;
                            handleTranslate(questionWithOptions);
                          }}
                        >
                          <img src="/images/translateiconactive.svg" alt="Translate" width="16" height="16" />
                          Translate in English
                        </button>
                        <button 
                          className="practice-action-btn" 
                          onClick={() => {
                            const questionWithOptions = `${exercise.questions[0].question}. Options: ${exercise.questions[0].options.map(opt => opt.text).join(', ')}`;
                            handleReadAloud(questionWithOptions, `question-${exercise.id}-0`);
                          }}
                          style={{ 
                            marginLeft: '8px'
                          }}
                        >
                          <img 
                            src={readingMessageId === `question-${exercise.id}-0` && isReading ? "/images/muteicon.svg" : "/images/readicon.svg"} 
                            alt={readingMessageId === `question-${exercise.id}-0` && isReading ? "Stop" : "Say It Aloud"} 
                            width="16" 
                            height="16" 
                          />
                          {readingMessageId === `question-${exercise.id}-0` && isReading ? "Stop" : "Say It Aloud"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* Conversation Messages (answers, feedback, subsequent questions) */}
        {exercise.conversationMessages &&
          exercise.conversationMessages.map((message, index) => {
            if (message.type === "user") {
              return (
                <div
                  key={`${exercise.id}-msg-${index}`}
                  className="practice-message-row user-message-row"
                  style={{ justifyContent: "flex-end", marginTop: "16px" }}
                >
                  <div
                    className="practice-message-content-wrapper"
                    style={{ maxWidth: "60%" }}
                  >
                    <div
                      className="practice-user-message-text"
                    >
                      {message.text}
                    </div>
                  </div>
                  <div
                    className="practice-avatar-circle user-avatar"
                    style={{ background: "#A100FF", marginLeft: "16px" }}
                  >
                    {userInitials}
                  </div>
                </div>
              );
            } else if (message.type === "bot" && message.isQuestion) {
              const questionIndex = exercise.questions.findIndex(
                (q) => q.id === message.questionData.id,
              );
              const isLatest = index === exercise.conversationMessages.length - 1;
              return (
                <div
                  key={`${exercise.id}-msg-${index}`}
                  className="practice-message-row bot-message-row"
                  style={{ marginTop: "16px" }}
                  ref={(el) => {
                    if (!isCompleted && isLatest) {
                      latestMessageRef.current = el;
                    }
                  }}
                >
                  <div className="practice-avatar-circle bot-avatar">
                    {avatarInitial}
                  </div>
                  <div className="practice-message-content-wrapper">
                    <div className="practice-bot-message-text">
                      {questionIndex + 1}. {message.text}
                    </div>
                    <div className="practice-questions-section">
                      <div className="practice-question-label">
                        Choose the correct answer
                      </div>
                      <div className="practice-radio-group">
                        {message.questionData.options.map((option) => (
                          <label
                            key={option.id}
                            className="practice-radio-option"
                          >
                            <input
                              type="radio"
                              name={`question-${exercise.id}-${message.questionData.id}`}
                              value={option.id}
                              checked={
                                exercise.answers[message.questionData.id] ===
                                option.id
                              }
                              onChange={() =>
                                !isCompleted &&
                                handleAnswerSelect(
                                  message.questionData.id,
                                  option.id,
                                )
                              }
                              disabled={isCompleted}
                            />
                            <span className="practice-radio-custom"></span>
                            <span className="practice-radio-text">
                              {option.text}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div className="practice-message-actions" style={{marginTop: '8px'}}>
                        <button 
                          className="practice-action-btn" 
                          onClick={() => {
                            const optionsText = (message.questionData.options || []).map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${opt.text}`).join('\n');
                            const questionWithOptions = `Question: ${message.questionData.question}\n\nAnswer Options:\n${optionsText}`;
                            handleTranslate(questionWithOptions);
                          }}
                        >
                          <img src="/images/translateiconactive.svg" alt="Translate" width="16" height="16" />
                          Translate in English
                        </button>
                        <button 
                          className="practice-action-btn" 
                          onClick={() => {
                            const questionWithOptions = `${message.questionData.question}. Options: ${message.questionData.options.map(opt => opt.text).join(', ')}`;
                            handleReadAloud(questionWithOptions, `question-${exercise.id}-${index}`);
                          }}
                          style={{ 
                            marginLeft: '8px'
                          }}
                        >
                          <img 
                            src={readingMessageId === `question-${exercise.id}-${index}` && isReading ? "/images/muteicon.svg" : "/images/readicon.svg"} 
                            alt={readingMessageId === `question-${exercise.id}-${index}` && isReading ? "Stop" : "Say It Aloud"} 
                            width="16" 
                            height="16" 
                          />
                          {readingMessageId === `question-${exercise.id}-${index}` && isReading ? "Stop" : "Say It Aloud"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } else if (message.type === "bot") {
              const isLatest = index === exercise.conversationMessages.length - 1;
              return (
                <div
                  key={`${exercise.id}-msg-${index}`}
                  className="practice-message-row bot-message-row"
                  style={{ marginTop: "16px" }}
                  ref={(el) => {
                    if (!isCompleted && isLatest) {
                      latestMessageRef.current = el;
                    }
                  }}
                >
                  <div className="practice-avatar-circle bot-avatar">
                    {avatarInitial}
                  </div>
                  <div className="practice-message-content-wrapper">
                    <div className="practice-feedback-message">
                      {message.isCorrect !== undefined && (
                        <img 
                          src={message.isCorrect ? "/images/Done.svg" : "/images/Error.svg"} 
                          alt={message.isCorrect ? "Correct" : "Incorrect"} 
                          style={{width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle'}}
                        />
                      )}
                      {message.text}
                    </div>
                    <div className="practice-message-actions" style={{ marginTop: "8px" }}>
                      <button 
                        className="practice-action-btn"
                        onClick={() => handleTranslate(message.text)}
                      >
                        <img
                          src="/images/translateiconactive.svg"
                          alt="Translate"
                          width="16"
                          height="16"
                        />
                        Translate in English
                      </button>
                      <button 
                        className="practice-action-btn"
                        onClick={() => handleReadAloud(message.text, `feedback-${exercise.id}-${index}`)}
                      >
                        <img
                          src={readingMessageId === `feedback-${exercise.id}-${index}` && isReading ? "/images/muteicon.svg" : "/images/readicon.svg"}
                          alt={readingMessageId === `feedback-${exercise.id}-${index}` && isReading ? "Stop" : "Say It Aloud"}
                          width="16"
                          height="16"
                        />
                        {readingMessageId === `feedback-${exercise.id}-${index}` && isReading ? "Stop" : "Say It Aloud"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })}
      </React.Fragment>
    );
  };

  return (
    <div className="practice-page">
      <Header user={user} />

      <div className="nav-header-container">
        <SideNav />
        <PageHeader
          breadcrumbs={breadcrumbs}
          language={languageToISOCode(language)}
          proficiencyLevel={levelToKebabCase(proficiencyLevel)}
          onLanguageChange={handleLanguageChange}
          onLevelChange={handleLevelChange}
          showProgress={false}
        />
      </div>

      <main className="practice-content">
        {/* Practice Title */}
        <div className="practice-title-section">
          <div className="practice-title-row">
            <h1 className="practice-title">
              Practice:
              <span className="practice-skill-dropdown" ref={skillDropdownRef}>
                <span
                  className="practice-skill-selector"
                  onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                >
                  <span className="practice-skill-name">Listening</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="#A100FF"
                      strokeWidth="2"
                    />
                  </svg>
                </span>
                {showSkillDropdown && (
                  <div className="practice-skill-dropdown-menu">
                    {skills.map((skill) => (
                      <div
                        key={skill.value}
                        className={`practice-skill-dropdown-item ${currentSkill === skill.value ? "active" : ""}`}
                        onClick={() => handleSkillChange(skill)}
                      >
                        {skill.label}
                      </div>
                    ))}
                  </div>
                )}
              </span>
            </h1>
            <button
              className={`practice-new-chat-btn ${hasSubmittedPrompt ? "active" : ""}`}
              onClick={handleNewChat}
              disabled={!hasSubmittedPrompt}
            >
              New Chat
            </button>
          </div>
          <div className="practice-title-line"></div>
        </div>

        {/* Main Content Area */}
        <div className="practice-main-area">
          {/* Left Side - Avatar */}
          <div className="practice-left-sidebar">
            <div className="practice-avatar-card">
              <img
                src={avatarImage}
                alt={`${avatarName} Avatar`}
                className="practice-avatar-image"
              />
              <div
                className="practice-avatar-sound-icon"
                onClick={handleAvatarSoundToggle}
              >
                <img
                  src={
                    isMuted ? "/images/muteicon.svg" : "/images/readicon.svg"
                  }
                  alt={isMuted ? "Muted" : "Unmuted"}
                  style={{ width: "100%", height: "100%" , display: "none"}}
                />
              </div>
              <div className="practice-avatar-info">
                <h2 className="practice-avatar-name">{avatarName}</h2>
                <button
                  className="practice-change-avatar"
                  onClick={handleChangeAvatar}
                >
                  Change Avatar
                </button>
              </div>
            </div>
          </div>

          {/* Right Content - Conversation */}
          <div className="practice-right-content" ref={rightContentRef}>
            {/* Welcome Section - Only show before first prompt submission */}
            {!hasSubmittedPrompt && (
              <div style={{ flexShrink: 0 }}>
                {/* Agent Section - Always visible and fixed */}
                <div className="practice-agent-section">
                  <div className="practice-agent-avatar">
                    <span>{avatarInitial}</span>
                  </div>
                  <div className="practice-agent-info">
                    <p className="practice-welcome-message">
                      {getWelcomeMessage()}
                    </p>
                    <div className="practice-actions">
                      <button className="practice-translate-chip" onClick={() => handleTranslate(getWelcomeMessage(), 'English', getSelectedLanguageName())}>
                        <img
                          src="/images/translateiconactive.svg"
                          alt="Translate"
                          width="18"
                          height="18"
                        />
                        Translate in {getSelectedLanguageName()}
                      </button>
                      <button 
                        className="practice-translate-chip" 
                        onClick={() => handleReadAloud(getWelcomeMessage(), 'welcome-message', 'english')}
                        style={{ marginLeft: '8px' }}
                      >
                        <img src={readingMessageId === 'welcome-message' && isReading ? "/images/muteicon.svg" : "/images/readicon.svg"} alt="Say It Aloud" width="18" height="15" />
                        {readingMessageId === 'welcome-message' && isReading ? 'Stop' : 'Say It Aloud'}
                      </button>
                    </div>

                    {/* Prompts Section - Inside agent-info for alignment */}
                    {showPrompts && (
                      <div className="practice-prompts-section">
                        <div className="practice-prompts-header">
                          <img
                            src="/images/Group.svg"
                            alt="Prompt icon"
                            width="12"
                            height="15"
                          />
                          <span>Explore Prompts</span>
                        </div>
                        <div className="practice-prompt-chips">
                          {isLoadingPrompts ? (
                            <div style={{ padding: "8px", color: "#707070" }}>
                              Loading prompts...
                            </div>
                          ) : displayedPrompts.length > 0 ? (
                            displayedPrompts.map((prompt, idx) => (
                              <button
                                key={idx}
                                className="practice-prompt-chip"
                                onClick={() => handlePromptClick(prompt)}
                              >
                                {prompt}
                              </button>
                            ))
                          ) : (
                            <div style={{ padding: "8px", color: "#707070" }}>
                              No prompts available
                            </div>
                          )}
                        </div>
                        {prompts.length > 3 && (
                          <button
                            className="practice-view-more-link"
                            onClick={() => setShowAllPrompts(!showAllPrompts)}
                          >
                            <img
                              src="/images/downarrowicon.png"
                              alt=""
                              width="16"
                              height="16"
                              style={{
                                transform: showAllPrompts
                                  ? "rotate(180deg)"
                                  : "none",
                              }}
                            />
                            {showAllPrompts ? "View less" : "View more"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Scrollable messages container */}
            <div
              className="practice-messages"
              ref={messagesContainerRef}
              style={{ flex: 1 }}
            >
              {/* Render all completed exercises */}
              {exercises.map((exercise) => renderExercise(exercise, true))}

              {/* Render current exercise */}
              {currentExercise && renderExercise(currentExercise, false)}
            </div>

            {/* Input Section - Fixed at bottom of right content */}
            <div className="practice-input-section" style={{ flexShrink: 0 }}>
              <div className="practice-input-container">
                <textarea
                  ref={inputRef}
                  className="practice-text-input"
                  placeholder={`Chat with ${avatarName}`}
                  value={inputText}
                  rows={1}
                  disabled={isLoading}
                  onChange={(e) => { setInputText(e.target.value); setShowPromptsOverlay(false); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; e.target.style.overflowY = e.target.scrollHeight > 120 ? 'auto' : 'hidden'; }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && inputText.trim() && !isLoading) {
                      e.preventDefault();
                      handleSendMessage();
                      e.target.style.height = 'auto';
                    }
                  }}
                />
                {hasSubmittedPrompt && (
                  <button
                    className="practice-input-icon-btn"
                    onClick={() => { if (!isLoading) setShowPromptsOverlay(!showPromptsOverlay); }}
                    style={isLoading ? {opacity: 0.4, pointerEvents: 'none'} : {}}
                  >
                    <img
                      src="/images/Group.svg"
                      alt="Prompts"
                      width="12"
                      height="15"
                    />
                    <span style={{ fontSize: "14px", marginLeft: "4px" }}>
                      Prompts
                    </span>
                  </button>
                )}
                <button
                  className={`voice-button ${sttActive ? 'recording' : ''}`}
                  onClick={handleMicClick}
                  aria-label="Voice input"
                  disabled={isLoading && !sttActive}
                  style={isLoading && !sttActive ? {opacity: 0.4} : {}}
                >
                  <img
                    src="/images/micicon.png"
                    alt="Mic"
                    width="20"
                    height="20"
                  />
                </button>
                <button
                  className="practice-send-btn"
                  onClick={() => handleSendMessage()}
                  disabled={!inputText.trim() || isLoading}
                  style={{
                    background: inputText.trim() && !isLoading ? "#A100FF" : "transparent",
                    opacity: isLoading ? 0.4 : 1
                  }}
                >
                  <img
                    src={
                      inputText.trim()
                        ? "/images/activeSend.svg"
                        : "/images/sendicon.png"
                    }
                    alt="Send"
                    width="20"
                    height="20"
                  />
                </button>
              </div>

              {/* Prompts Overlay */}
              {showPromptsOverlay && (
                <div className="practice-prompts-overlay" ref={promptsOverlayRef}>
                  <div className="practice-prompts-overlay-header">
                    <span>Explore prompt library</span>
                  </div>
                  <div className="practice-prompts-overlay-content">
                    {prompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        className="practice-prompt-overlay-chip"
                        onClick={() => {
                          handlePromptClick(prompt);
                          setShowPromptsOverlay(false);
                        }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="practice-disclaimer">
                Response generated by AI. Verify accuracy and privacy
                restrictions before use.
                <button className="disclaimer-link" onClick={() => setShowTermsModal(true)}>Terms of use</button>
                <button className="disclaimer-link" onClick={() => setShowPrivacyModal(true)}>Privacy policy</button>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Translation Panel */}
      {showTranslation && (
        <div className="translation-panel" ref={translationPanelRef}>
          <div className="translation-header">
            <h3>Translation</h3>
            <button className="close-button" onClick={handleCloseTranslation}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#000000"/>
              </svg>
            </button>
          </div>
          
          <div className="translation-content">
            <div className="translation-section">
              <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                <h4 style={{margin: 0}}>{translationLabel} Translation</h4>
                <button 
                  className="action-chip" 
                  onClick={handleReadTranslation}
                  disabled={!translatedText || translatedText === 'Translating...'}
                  style={{opacity: (!translatedText || translatedText === 'Translating...') ? 0.4 : 1}}
                >
                  <img 
                    src={isReadingTranslation ? "/images/muteicon.svg" : "/images/readicon.svg"} 
                    alt={isReadingTranslation ? "Stop" : "Say It Aloud"} 
                    width="16" 
                    height="16" 
                    style={{ marginRight: '4px' }}
                  />
                  <span>{isReadingTranslation ? 'Stop' : 'Say It Aloud'}</span>
                </button>
              </div>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>{translatedText}</div>
            </div>
          </div>
        </div>
      )}



      <Footer />

      {showPrivacyModal && (
        <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="footer-privacy-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Privacy Policy</h2>
                <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Data Privacy Statement</h3>
                <p>Please be aware that any personal data that you and others provide through this tool may be processed by Accenture.</p>
                <p>The protection of your personal data is very important to Accenture. Accenture is committed to keeping your personal data secure, and processing it in accordance with applicable data protection laws and our internal policies including our <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">Global Data Privacy Policy</a>.</p>
                <p>Before providing any personal data through this tool, Accenture invites you to read its <a href="https://www.accenture.com/us-en/about/privacy-policy" target="_blank" rel="noopener noreferrer">privacy statement</a>, which includes important information on why and how Accenture is processing your personal data.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowPrivacyModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="footer-term-content">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Terms of Use</h2>
                <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Acceptance of Terms</h3>
                <p>By accessing and using Language Buddy, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.</p>
                <h3>License and Access</h3>
                <p>Language Buddy grants you a limited, non-exclusive, non-transferable license to access and use the platform for personal language learning purposes.</p>
                <h3>User Conduct</h3>
                <p>You agree to use Language Buddy in accordance with all applicable laws and regulations.</p>
                <h3>Intellectual Property</h3>
                <p>All content, materials, features, and functionality available on Language Buddy are the property of Accenture and are protected by copyright, trademark, and other intellectual property laws.</p>
                <h3>Disclaimer of Warranties</h3>
                <p>Language Buddy is provided "as is" without warranties of any kind, either express or implied.</p>
              </div>
              <div className="modal-footer">
                <button className="modal-agree-button" onClick={() => setShowTermsModal(false)}>I Agree</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListeningPage;

